import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { groupedMatrix, ROLE_LABELS, ROLE_DESCRIPTIONS, ORDERED_ROLES } from "@/lib/permissions";
import { Check, X, ShieldCheck } from "lucide-react";

const GROUP_LABELS = {
  dashboard: "Dashboard",
  search: "Search",
  orders: "Orders",
  contacts: "Contacts",
  deals: "Sales Pipeline",
  catalog: "Catalog",
  collections: "Collections",
  gold_rates: "Metal Rates",
  production: "Production",
  cad: "CAD",
  invoices: "Invoices",
  settings: "Settings",
  tracker: "Customer Tracker",
};

export default function RoleMatrixPage() {
  const grouped = groupedMatrix();

  return (
    <div className="space-y-6" data-testid="role-matrix-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Access Control</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">Role Permissions</h1>
        <p className="text-[#4A5A50] mt-1">Read-only view of every capability by role. Enforced on both UI and API.</p>
      </div>

      {/* Role definitions */}
      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-4 flex items-center gap-2">
            <ShieldCheck size={12}/> Role definitions
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ORDERED_ROLES.map(r => (
              <div key={r} className="border border-[#E4DFCF] rounded-lg p-3" data-testid={`role-def-${r}`}>
                <div className="font-serif text-lg text-[#083F22]">{ROLE_LABELS[r]}</div>
                <div className="text-xs text-[#4A5A50] mt-1 leading-relaxed">{ROLE_DESCRIPTIONS[r]}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-[#F6EED8] rounded-lg text-xs text-[#6E5416]">
            <strong>Privacy rule:</strong> Production users never see customer identity or pricing. The backend strips these fields before returning.
          </div>
        </CardContent>
      </Card>

      {/* Capability matrix per group */}
      {Object.entries(grouped).map(([group, caps]) => (
        <Card key={group} className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <div className="px-6 py-4 bg-[#F1EDDF] border-b border-[#E4DFCF]">
              <div className="font-serif text-lg">{GROUP_LABELS[group] || group}</div>
            </div>
            <table className="min-w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#4A5A50]">
                <tr>
                  <th className="px-4 py-3 text-left">Capability</th>
                  {ORDERED_ROLES.map(r => <th key={r} className="px-3 py-3 text-center">{ROLE_LABELS[r]}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4DFCF]">
                {caps.map(({ capability, roles }) => (
                  <tr key={capability} data-testid={`matrix-row-${capability}`}>
                    <td className="px-4 py-2.5 font-medium text-xs">{capability}</td>
                    {ORDERED_ROLES.map(r => {
                      const allowed = r === "admin" || roles.includes(r);
                      return (
                        <td key={r} className="px-3 py-2.5 text-center">
                          {allowed ? <Check size={16} className="text-[#0F5E39] mx-auto"/> : <X size={14} className="text-[#E4DFCF] mx-auto"/>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
